import { RotateCw } from "lucide-react";

import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { cn } from "../../lib/utils";
import { getCrawlerPreferenceRefreshTooltip } from "../utils";

import { ActionButton } from "./shared/ActionButton";

import type { CrawlerPreferencesState } from "../hooks/useCrawlerPreferencesState";

const getPreferenceTypeLabel = (type: string) => {
  return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

interface CrawlerPreferenceSelectorProps {
  source: string;
  state: CrawlerPreferencesState;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

/**
 * The folder/channel tree picker.
 *
 * Presentational — the caller owns the state via useCrawlerPreferencesState and
 * reads `state.preferencesForSave` when it saves, so the same picker serves a user's own
 * integration and a workspace configured under a deployment parent.
 */
export function CrawlerPreferenceSelector({
  source,
  state,
  onRefresh,
  isRefreshing,
}: CrawlerPreferenceSelectorProps) {
  const {
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    handleItemToggle,
    handleSelectAll,
    getTabStats,
    getFilteredItems,
    hasDriveMetadata,
    availableDrives,
    selectedDriveId,
    setSelectedDriveId,
    currentDrivePreferences,
  } = state;

  return (
    <div className="space-y-3">
      {hasDriveMetadata && availableDrives.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-zinc-900 dark:text-white">
            Select Drive
          </label>
          <Select value={selectedDriveId} onValueChange={setSelectedDriveId}>
            <SelectTrigger className="w-full bg-white dark:bg-neutral-800 border-zinc-200 dark:border-neutral-700">
              <SelectValue placeholder="Select a drive" />
            </SelectTrigger>
            <SelectContent>
              {availableDrives.map((drive) => (
                <SelectItem key={drive.drive_id} value={drive.drive_id || ""}>
                  {drive.drive_name}
                  {drive.drive_type === "my_drive" && " (My Drive)"}
                  {drive.drive_type === "shared_drive" && " (Shared Drive)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {currentDrivePreferences.length === 1 && (
        <div className="flex items-center">
          <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
            {getPreferenceTypeLabel(currentDrivePreferences[0].type)}
          </h3>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {currentDrivePreferences.length > 1 && (
          <div className="w-full">
            <TabsList
              className="grid w-full bg-zinc-100 dark:bg-neutral-900 rounded-lg p-1"
              style={{
                gridTemplateColumns: `repeat(${currentDrivePreferences.length}, minmax(120px, 1fr))`,
              }}
            >
              {currentDrivePreferences.map((preference) => (
                <TabsTrigger
                  key={preference.type}
                  value={preference.type}
                  className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-neutral-700 dark:data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md dark:text-neutral-400 transition-all text-center"
                >
                  {getPreferenceTypeLabel(preference.type)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        )}
        {currentDrivePreferences.map((preference) => {
          const filteredItems = getFilteredItems(preference.data);
          const stats = getTabStats(preference.type);
          const isAllSelected =
            stats.selected === stats.total && stats.total > 0;

          return (
            <TabsContent
              key={preference.type}
              value={preference.type}
              className="mt-4"
            >
              <div className="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-4 h-[clamp(180px,38vh,320px)] flex flex-col">
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`select-all-${preference.type}`}
                      checked={isAllSelected}
                      onCheckedChange={() => handleSelectAll(preference.type)}
                      className={cn(
                        stats.selected > 0 &&
                          stats.selected < stats.total &&
                          "data-[state=checked]:bg-zinc-900 dark:data-[state=checked]:bg-white",
                      )}
                    />
                    <label
                      htmlFor={`select-all-${preference.type}`}
                      className="text-sm font-medium text-zinc-950 dark:text-white"
                    >
                      Select all ({stats.selected}/{stats.total})
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    {onRefresh && (
                      <Tooltip>
                        {/* ActionButton forwards no ref for asChild to anchor to. */}
                        <TooltipTrigger asChild>
                          <span tabIndex={0}>
                            <ActionButton
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 border-zinc-200"
                              onClick={onRefresh}
                              loading={isRefreshing}
                              icon={<RotateCw className="w-4 h-4" />}
                            />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {getCrawlerPreferenceRefreshTooltip(source)}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <div className="relative">
                      <Input
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-[200px] h-8 pr-3 text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="h-px bg-zinc-200 dark:bg-neutral-600 mb-3 flex-shrink-0" />
                <div className="flex-1 overflow-y-auto">
                  <div className="space-y-3 pr-2">
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item) => {
                        const isFailed = item.active === false;
                        return (
                          <div key={item.source_id} className="flex flex-col">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={item.source_id}
                                checked={item.status}
                                disabled={isFailed}
                                onCheckedChange={() =>
                                  handleItemToggle(
                                    preference.type,
                                    item.source_id,
                                  )
                                }
                              />
                              <label
                                htmlFor={item.source_id}
                                className={cn(
                                  "text-sm font-medium",
                                  isFailed
                                    ? "text-zinc-400 dark:text-zinc-500 cursor-default"
                                    : "text-zinc-950 dark:text-white cursor-pointer",
                                )}
                              >
                                {item.name}
                              </label>
                            </div>
                            {isFailed && (
                              <p className="text-xs text-red-500 ml-6 mt-0.5">
                                {item.error_message || "Failed to crawl"}
                              </p>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">
                          {searchQuery
                            ? "No items match your search"
                            : "No items available"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
